import axios from 'axios';
import { store } from '../redux-toolkit/redux_store/store';
import { isSuperadminTokenExpired, getNewSuperadminRefreshToken, handleSuperadminLogoutAndRedirect } from './superadminTokenRefresh';

// Create a separate axios instance for superadmin
export const superadminAxios = axios.create({
  baseURL: process.env.REACT_APP_SUPERADMIN_BASEURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
superadminAxios.interceptors.request.use(
  async (config) => {
    const state = store.getState();
    const { accessTokenExpiry } = state.superadminRefreshTokenReducer;
    const token = localStorage.getItem('superadmin_token');

    // Add token to headers if available
    if (token) {
      config.headers.Authorization = token;
    }

    // Skip token refresh for login and refresh endpoints
    const skipUrls = ['/loginUserSuperadminPanel', '/getNewToken'];
    if (skipUrls.some(url => config.url.includes(url))) {
      return config;
    }

    // Check if token needs refresh
    if (isSuperadminTokenExpired(accessTokenExpiry)) {
      try {
        await getNewSuperadminRefreshToken();
        const newToken = localStorage.getItem('superadmin_token');
        
        if (newToken) {
          config.headers.Authorization = newToken;
        }
      } catch (error) {
        // Only logout for permanent failures (403, Invalid Token)
        const isPermanentFailure = error.response?.status === 403 || 
                                  error.message.includes('Invalid Token') ||
                                  error.message.includes('Request failed with status code 403');
        
        if (isPermanentFailure) {
          console.log('[Superadmin Axios Interceptor] Permanent failure detected, logging out:', error.message);
          handleSuperadminLogoutAndRedirect();
        } else {
          console.log('[Superadmin Axios Interceptor] Temporary refresh failure, continuing without logout:', error.message);
        }
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
superadminAxios.interceptors.response.use(
  (response) => {
    // Check for Invalid Token in successful responses
    if (response.data?.status === false && response.data?.message === "Invalid Token") {
      console.log('[Superadmin Axios Interceptor] Invalid token detected in response');
      handleSuperadminLogoutAndRedirect();
      return Promise.reject(new Error('Invalid Token'));
    }
    return response;
  },
  async (error) => {
    // Check for Invalid Token in error responses
    if (error.response?.data?.status === false && error.response?.data?.message === "Invalid Token") {
      console.log('[Superadmin Axios Interceptor] Invalid token detected in error response');
      handleSuperadminLogoutAndRedirect();
      return Promise.reject(error);
    }

    // For 401 errors, try to refresh token once
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;

      try {
        await getNewSuperadminRefreshToken();
        const newToken = localStorage.getItem('superadmin_token');
        
        if (newToken) {
          error.config.headers.Authorization = newToken;
          return superadminAxios(error.config);
        }
      } catch (refreshError) {
        // Only logout for permanent failures (403, Invalid Token)
        const isPermanentFailure = refreshError.response?.status === 403 || 
                                  refreshError.message.includes('Invalid Token') ||
                                  refreshError.message.includes('Request failed with status code 403');
        
        if (isPermanentFailure) {
          console.log('[Superadmin Axios Interceptor] Permanent refresh failure detected, logging out:', refreshError.message);
          handleSuperadminLogoutAndRedirect();
        } else {
          console.log('[Superadmin Axios Interceptor] Temporary refresh failure, continuing without logout:', refreshError.message);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const setupSuperadminAxiosInterceptors = () => {
  console.log('[Superadmin Axios] Interceptors set up');
};